import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Video, ArrowLeft, Calendar, Key, CalendarDays, CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CAPS, hasCapability } from '../../utils/capabilities';
import ZoomAccountManagement from '../../components/admin/ZoomAccountManagement';
import LiveClassScheduler from '../../components/admin/LiveClassScheduler';
import LiveClassesCalendar from '../../components/admin/LiveClassesCalendar';
import CompletedClassesTab from '../../components/admin/CompletedClassesTab';
import LicenseSchedulePanel from '../../components/admin/LicenseSchedulePanel';
import UpcomingClassesTab from '../../components/admin/UpcomingClassesTab';

const LiveClassesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFullAdmin = user?.role === 'admin';
  // Phase 4d: capabilities decide the surface a sub-admin sees.
  const canManage = hasCapability(user, CAPS.MANAGE_CLASSES); // implies host_classes
  const canHost = hasCapability(user, CAPS.HOST_CLASSES);
  // Zoom license + full license-schedule board are infrastructure — full admin only.
  const canSeeInfra = isFullAdmin;

  // Hide-not-gate: if a sub-admin reaches this page via direct URL with
  // neither host_classes nor manage_classes, redirect them to /admin.
  useEffect(() => {
    if (user && !isFullAdmin && !canHost && !canManage) {
      navigate('/admin', { replace: true });
    }
  }, [user, isFullAdmin, canHost, canManage, navigate]);

  const [tab, setTab] = useState('upcoming');

  return (
    <Layout>
      <div className="page-transition" data-testid="live-classes-admin-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-4"
          data-testid="live-classes-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Admin Dashboard
        </Button>

        <Card className="mb-6 border-l-4 border-l-[#FF6B6B] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#FF6B6B]/5 to-[#0A7EA4]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#FF6B6B] rounded-lg">
                <Video className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl text-[#0A7EA4]">Live Classes</CardTitle>
                <CardDescription className="text-base mt-1">
                  {canSeeInfra
                    ? 'Calendar overview, class scheduling, and Zoom license management'
                    : canManage
                      ? 'Schedule, edit, and host classes'
                      : 'Your upcoming classes and calendar view'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap h-auto">
            {/* Everyone with host_classes or manage_classes sees these two */}
            {(canHost || canManage) && (
              <>
                <TabsTrigger value="upcoming" data-testid="live-classes-tab-upcoming">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Upcoming Classes
                </TabsTrigger>
                <TabsTrigger value="calendar" data-testid="live-classes-tab-calendar">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Calendar
                </TabsTrigger>
              </>
            )}
            {/* Only manage_classes (or admin) sees the scheduling + completed tabs */}
            {canManage && (
              <>
                <TabsTrigger value="completed" data-testid="live-classes-tab-completed">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completed Classes
                </TabsTrigger>
                <TabsTrigger value="classes" data-testid="live-classes-tab-classes">
                  <Calendar className="w-4 h-4 mr-2" />
                  Scheduled Classes
                </TabsTrigger>
              </>
            )}
            {/* Only full admin sees Zoom infra */}
            {canSeeInfra && (
              <>
                <TabsTrigger value="licenses" data-testid="live-classes-tab-licenses">
                  <Key className="w-4 h-4 mr-2" />
                  Zoom Licenses
                </TabsTrigger>
                <TabsTrigger value="schedule" data-testid="live-classes-tab-schedule">
                  <Clock className="w-4 h-4 mr-2" />
                  License Schedule
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="upcoming">
            <UpcomingClassesTab />
          </TabsContent>
          <TabsContent value="calendar">
            <LiveClassesCalendar />
          </TabsContent>
          {canManage && (
            <>
              <TabsContent value="completed">
                <CompletedClassesTab />
              </TabsContent>
              <TabsContent value="classes">
                <LiveClassScheduler canSchedule={true} />
              </TabsContent>
            </>
          )}
          {canSeeInfra && (
            <>
              <TabsContent value="licenses">
                <ZoomAccountManagement />
              </TabsContent>
              <TabsContent value="schedule">
                <LicenseSchedulePanel />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default LiveClassesPage;
